import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFolder } from "@/types/dataRoom"

interface DataRoomUploadTargetDialogProps {
  open: boolean
  folders: DataRoomFolder[]
  onChoose: (folderId: string | null) => void
  onClose: () => void
}

/** Asked before a root upload: the room's whole point is that documents
 * land in the matching folder, so the choice comes first, not as an
 * afterthought the customer cannot fix later. */
export const DataRoomUploadTargetDialog: React.FC<
  DataRoomUploadTargetDialogProps
> = ({ open, folders, onChoose, onClose }) => {
  const { translate } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("DATA_ROOM/UPLOAD_TARGET_TITLE")}
          </DialogTitle>
        </DialogHeader>
        <Typography variant="textS" className="text-grey5">
          {translate("DATA_ROOM/UPLOAD_TARGET_HELP")}
        </Typography>
        <div
          className="flex flex-col gap-1"
          data-testid="data-room-upload-target"
        >
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => onChoose(folder.id)}
              className="flex flex-col items-start rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-grey2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Typography variant="textS-bold">{folder.name}</Typography>
              {folder.description && (
                <Typography variant="textXS" className="text-grey5">
                  {folder.description}
                </Typography>
              )}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={() => onChoose(null)}
          >
            {translate("DATA_ROOM/UPLOAD_TARGET_NONE")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
